'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { ArrowsInSimpleIcon, ArrowsOutSimpleIcon, BugIcon } from '@phosphor-icons/react';
import { Box, Dialog, IconButton, Stack, StateCard, Tooltip } from '@unity/cloud-ui';
import { useTheme } from '@unity/cloud-ui/styles';
import DOMPurify from 'dompurify';
import { MermaidConfig } from './initialize';

const MermaidContent = (props) => {
  const { toggleDialog, isDialogOpen, renderingError, svg } = props;
  const t = useTranslations();

  return (
    <Box>
      <Box py={1} height="100%" display="flex" flexDirection="column">
        {!renderingError && (
          <Stack alignItems="flex-end" paddingX={1}>
            <Tooltip title={t('mdx:code:code-wrapper:toggle-fullscreen')} placement="top">
              <IconButton aria-label={t('mdx:code:code-wrapper:toggle-fullscreen')} onClick={toggleDialog}>
                {isDialogOpen ? (
                  <ArrowsInSimpleIcon weight="fill" size={24} />
                ) : (
                  <ArrowsOutSimpleIcon weight="fill" size={24} />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        <Box
          sx={{
            display: renderingError ? 'none' : 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
          }}
          dangerouslySetInnerHTML={
            svg
              ? {
                  __html: DOMPurify.sanitize(svg, {
                    ADD_TAGS: ['foreignObject'],
                    HTML_INTEGRATION_POINTS: { foreignobject: true },
                  }),
                }
              : undefined
          }
        />
        <Box>{renderingError && <StateCard title={t('mdx:mermaid:mermaid-render-error')} icon={BugIcon} />}</Box>
      </Box>
    </Box>
  );
};

export const Mermaid = (props: { children?: string }) => {
  const { children } = props;
  const { palette } = useTheme();
  const diagramId = React.useId();
  const [renderingError, setRenderingError] = React.useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [svg, setSvg] = React.useState<string | null>(null);

  const toggleDialog = () => {
    setIsDialogOpen((prevState) => !prevState);
  };

  React.useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const graphDefinition = children?.replace(/`/g, '\\`');
        const graphWithTheme = `%%{
            init: {
              'theme': 'base',
              'themeVariables': {
                'primaryColor': '${palette.primary.lighter}',
                'primaryTextColor': '${palette.text.primary}',
                'primaryBorderColor': '${palette.default.outlinedBorder}',
                'lineColor': '${palette.primary.main}',
                'background': '${palette.background.default}',
                'secondaryColor': '${palette.chart.series1.solid}',
                'secondaryTextColor': '${palette.text.primary}',
                'tertiaryColor': '${palette.common.inverseWhite}',
                'labelTextColor': 'white',
                'pieStrokeWidth': 1,
                'pieOuterStrokeWidth': 1,
                'pie1': '${palette.chart.series1.solid}',
                'pie2': '${palette.chart.series2.solid}',
                'pie3': '${palette.chart.series3.solid}',
                'pie4': '${palette.chart.series4.solid}',
                'pie5': '${palette.chart.series5.solid}',
                'pie6': '${palette.chart.series6.solid}',
                'pie7': '${palette.chart.series7.solid}',
                'pie8': '${palette.chart.series8.solid}',
                'pie9': '${palette.chart.series9.solid}',
                'pie10': '${palette.chart.series10.solid}',
                'pie11': '${palette.chart.series11.solid}',
                'pie12': '${palette.chart.series12.solid}'
              }
            }
          }%%${graphDefinition}`;
        const { svg } = await MermaidConfig.get().render(`mermaid-${diagramId}`, graphWithTheme);
        if (isMounted) {
          setSvg(svg);
          setRenderingError(false);
        }
      } catch (_) {
        if (isMounted) setRenderingError(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [children, diagramId, palette]);

  return (
    <>
      <MermaidContent
        isDialogOpen={isDialogOpen}
        toggleDialog={toggleDialog}
        renderingError={renderingError}
        svg={svg}
      />
      <Dialog fullScreen open={isDialogOpen} onClose={toggleDialog} sx={{ zIndex: 2101 }}>
        <MermaidContent
          isDialogOpen={isDialogOpen}
          toggleDialog={toggleDialog}
          renderingError={renderingError}
          svg={svg}
        />
      </Dialog>
    </>
  );
};
